import axios, { AxiosInstance } from "axios";
import type { Store } from "@reduxjs/toolkit";
import type { Appstore } from "../redux/store";
import { hideLoader, showLoader } from "../redux/loaderActions";
import { setPrevPathIfAllowed } from "./logout";
import { clearApplicationCache } from "./clearApplicationCache";
import { resolveAuthErrorPayload } from "./authErrorPayload";

/** Avoid static import of store: axios → store → userPreferences → UserPreferences → axios (TDZ in Axios). */
function getStore(): Store<Appstore> {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require("../redux/store").default;
}

let baseURL: string;
let axiosInstance: AxiosInstance;

const getCookie = (name: string) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
};

let requestCount = 0;
let sessionEnding = false;

export const incrementLoaderCount = () => {
  if (typeof window !== "undefined") {
    requestCount++;
    if (requestCount === 1) {
      showLoader();
    }
  }
};

export const decrementLoaderCount = () => {
  if (typeof window !== 'undefined') {
    requestCount--;
    if (requestCount === 0) {
      hideLoader();
    }
    if (requestCount < 0) {
      requestCount = 0;
    }
  }
};

export default class Axios {
  constructor() {
    baseURL = (process.env.REACT_APP_ROOT_API as string)
    axiosInstance = axios.create(
      {
        baseURL: baseURL,
        timeout: 400000
      }
    )
    axiosInstance.interceptors.request.use(function (config: any) {
      if (typeof window !== 'undefined' && !config.url.includes('/password-reset')) {
        const method = (config.method || 'get').toLowerCase();
        const isGet = method === 'get';
        const isLoginRequest = config.url?.includes('/login');
        if (isGet || isLoginRequest) {
          requestCount++;
          if (requestCount === 1) {
            showLoader();
          }
        }
        let token: string | null = sessionStorage.getItem('token');
        let decoded: string | null = sessionStorage.getItem('token_decoded');
        if (!token) {
          token = getCookie('token');
        }
        if (token) {
          config.headers.Authorization = 'Bearer ' + token;
        }

        if (!decoded) {
          decoded = getCookie('token_decoded');
        }

        if (decoded) {
          const userRole: string = JSON.parse(decoded)?.extension_WSG_Permissions as string;
          if (userRole) {
            config.headers['user-role'] = userRole;
          }
        }

        const currentPath = window.location.pathname;
        config.headers['x-frontend-route'] = currentPath;
      }
      return config;
    }, function (error: any) {
      console.error(error);
      return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
            (response: any) => {
                const method = (response?.config?.method || 'get').toLowerCase();
                const isLoginRequest = response?.config?.url?.includes('/login');
                if (method === 'get' || isLoginRequest) {
                    requestCount--;
                    if (requestCount === 0) {
                        hideLoader();
                    }
                }
                return response;
            },
            (error) => {
                const method = (error?.config?.method || 'get').toLowerCase();
                const isLoginRequest = error?.config?.url?.includes('/login');
                if (method === 'get' || isLoginRequest) {
                    requestCount--;
                    if (requestCount === 0) {
                        hideLoader();
                    }
                }
                return Promise.reject(error);
            }
        );

    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const eraseCookie = (name: string) => {
          document.cookie = name + '=; Max-Age=-99999999;';
        };

        const status = error.response?.status;
        const authError = resolveAuthErrorPayload(error.response?.data);
        const accountDisabled = authError.isAccountDisabled;
        const currentPath = window.location.pathname.toLowerCase();
        const isLandingPage = currentPath === '/' || currentPath.startsWith('/landing-page-client') || currentPath.startsWith('/landing-page-ssr');

        // Rutas de auth: no redirigir en 401 para que el usuario vea el mensaje de error (ej. credenciales incorrectas)
        const authRoutes = ["/login", "/two-factor-auth", "/link-account-login", "/forgot-password", "/confirmlogin"];
        const isAuthRoute = authRoutes.some((route) => currentPath === route || currentPath.startsWith(route + "/"));

        const publicRoutes = [
          "/aboutus",
          "/benefits",
          "/objectives",
          "/join",
          "/board",
          "/councils",
          "/events",
          "/joinwsg.asp",
          "/wsg_practicegroups.asp",
          "/profile",
          "/contacts.asp",
          "/contact.asp",
          "/contact",
          "/customsearch.asp",
          "/aboutus.asp",
          "/wsg_members.asp",
          "/wsg_groups.asp",
          "/gcinsights.asp",
          "/gcinsights",
          "/blog",
          "/wsgbynumbers.asp",
          "/wsg_objectives.asp",
          "/bod.asp",
          "/council.asp",
          "/wsg_practicegroupregions.asp",
          "/meeting2",
          "/meetingcontacts",
          "/publications.asp",
          "/releases.asp",
          "/success_stories.asp",
          "/newsfeeds",
          "/wsg-knowledge-center.asp",
          "/manage_events.asp",
          "/meetingregistrants",
          "/meetingagenda",
          "/secondments.asp",
          "/dealmaker.asp",
          "/help.asp",
          "/member2",
          "/member",
          "/members",
          "/members-locations.asp",
          "/meetingphotos",
          "/visitorinfo",
          "/sitemap.asp",
          "/privacypolicy.asp",
          "/disclaimer.asp",
          "/default.asp",
          "/membership-dues",
          "/bylaws",
          "/meetingpayment",
          "/search-special.asp",
          "/advanced-search.asp",
          "/member-search",
          "/email-subscriptions",
          "/unsubscribed",
          "/email-subscription.asp",
          "/press-subscription.asp",
          "/networkwire-subscription.asp",
          "/publications-subscription.asp",
          "/stories-subscription.asp",
        ];

        const isPublicRoute = publicRoutes.some((route) => {
          return currentPath === route || currentPath.startsWith(route + "/");
        });

        if (
          (status === 401 || status === 403) &&
          !accountDisabled &&
          (isLandingPage || isPublicRoute || isAuthRoute)
        ) {
          return Promise.reject(error);
        }

        if (accountDisabled && isAuthRoute) {
          return Promise.reject(error);
        }

        if (accountDisabled || status === 401) {
          if (sessionEnding) {
            return Promise.reject(error);
          }
          sessionEnding = true;
          if (!accountDisabled) {
            const fullPath = window.location.pathname + window.location.search;
            setPrevPathIfAllowed(fullPath);
          }
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("refresh_token");
          sessionStorage.removeItem("access_token");
          sessionStorage.removeItem("access_timestamp");
          sessionStorage.removeItem("token_decoded");

          eraseCookie("token");
          eraseCookie("refresh_token");
          eraseCookie("access_token");
          eraseCookie("access_timestamp");
          eraseCookie("token_decoded");

          localStorage.setItem("logout", Date.now().toString());
          const loginPath = accountDisabled
            ? `/login?error=${encodeURIComponent(authError.message)}`
            : "/login";
          void clearApplicationCache().finally(() => {
            window.location.href = loginPath;
          });
          return Promise.reject(error);
        }
        throw error;
      }
    );
  }

  async Get(path: string, id?: string) {
    if (id) {
      return await axiosInstance.get(`${path}?id=${id}`)
    }
    return await axiosInstance.get(path)
  }

  async GetArrayBuffer(path: string) {
    return await axiosInstance.get(path, { responseType: "arraybuffer" });
  }

  async Post(path: string, data: any) {
    return await axiosInstance.post(path, data)
  }

  async Put(path: string, data: any) {
    return await axiosInstance.put(`${path}?id=${data.id}`, data);
  }

  async PutDataIntoModel(path: string, data: any) {
    return await axiosInstance.put(path, data)
  }

  async Delete(path: string, id: string) {
    return await axiosInstance.delete(`${path}?id=${id}`)
  }

  async DeleteByPath(path: string) {
    return await axiosInstance.delete(path)
  }

  async Patch(path: string, data: any) {
    return await axiosInstance.patch(`${path}?id=${data.id}`, data)
  }

  async PatchBody(path: string, data: any) {
    return await axiosInstance.patch(path, data)
  }
}
